import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Edit2, Save, Eye, EyeOff } from "lucide-react";
import { api, fetchCollections } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";

const blank = {
  slug: "",
  name_en: "",
  name_pt: "",
  image_url: "",
  sort_order: 0,
  show_in_shop_menu: true,
};

export const AdminCollectionsTab = () => {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingSlug, setEditingSlug] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/collections");
    setItems(data || []);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const reset = () => {
    setForm(blank);
    setEditingSlug(null);
    setShowCreate(false);
    setError("");
  };

  const startEdit = (c) => {
    setEditingSlug(c.slug);
    setForm({
      slug: c.slug,
      name_en: c.name_en || "",
      name_pt: c.name_pt || "",
      image_url: c.image_url || "",
      sort_order: c.sort_order ?? 0,
      show_in_shop_menu: c.show_in_shop_menu !== false,
    });
    setShowCreate(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        slug: form.slug,
        name_en: form.name_en,
        name_pt: form.name_pt || form.name_en,
        image_url: form.image_url || null,
        sort_order: Number(form.sort_order) || 0,
        show_in_shop_menu: !!form.show_in_shop_menu,
      };
      if (editingSlug) {
        await api.patch(`/admin/collections/${editingSlug}`, payload);
      } else {
        await api.post("/admin/collections", payload);
      }
      await load();
      reset();
      // Refresh public collections cache (header/shop will refetch on next mount).
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMenu = async (c) => {
    try {
      await api.patch(`/admin/collections/${c.slug}`, {
        show_in_shop_menu: !c.show_in_shop_menu,
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete collection "${c.name_en}"? Products using it must be reassigned first.`)) return;
    try {
      await api.delete(`/admin/collections/${c.slug}`);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section data-testid="admin-collections-section">
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ COLLECTIONS</p>
        <button
          onClick={() => (showCreate ? reset() : setShowCreate(true))}
          className="bg-kult-pink text-black font-display text-base px-4 py-2 hover:bg-kult-yellow inline-flex items-center gap-2"
          data-testid="collections-toggle-create"
        >
          {showCreate ? <X size={16} /> : <Plus size={16} />} {showCreate ? "CLOSE" : "NEW COLLECTION"}
        </button>
      </div>

      <p className="text-kult-muted text-sm mb-6">
        Create custom shop categories (e.g. <em>Limited</em>, <em>Sale</em>, <em>Tour</em>). Toggle which ones appear inside the Shop dropdown.
      </p>

      {showCreate && (
        <form
          onSubmit={submit}
          className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="admin-collection-form"
        >
          <h3 className="md:col-span-2 font-display text-2xl">
            {editingSlug ? `EDIT · ${editingSlug}` : "NEW COLLECTION"}
          </h3>
          <Field
            label="SLUG (lowercase, no spaces — e.g. limited)"
            required
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            testId="collection-slug"
          />
          <Field
            label="SORT ORDER (lower = first)"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            testId="collection-sort"
          />
          <Field
            label="NAME (EN)"
            required
            value={form.name_en}
            onChange={(e) => setForm({ ...form, name_en: e.target.value })}
            testId="collection-name-en"
          />
          <Field
            label="NAME (PT)"
            value={form.name_pt}
            onChange={(e) => setForm({ ...form, name_pt: e.target.value })}
            testId="collection-name-pt"
          />
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">
              CARD IMAGE (optional, 800×1200 portrait)
            </p>
            <ImageUpload
              value={form.image_url ? [form.image_url] : []}
              onChange={(urls) => setForm({ ...form, image_url: urls[0] || "" })}
              max={1}
              testIdPrefix="collection-image"
              recommendedSize="800×1200 (2:3 portrait)"
            />
          </div>
          <label className="md:col-span-2 inline-flex items-center gap-3 text-sm font-mono text-white cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.show_in_shop_menu}
              onChange={(e) => setForm({ ...form, show_in_shop_menu: e.target.checked })}
              data-testid="collection-show-in-menu"
              className="w-4 h-4 accent-kult-pink"
            />
            <span className="tracking-[0.2em] text-xs">SHOW INSIDE SHOP DROPDOWN MENU</span>
          </label>
          {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="collection-submit"
          >
            <Save size={18} /> {submitting ? "SAVING..." : editingSlug ? "SAVE" : "CREATE"}
          </button>
        </form>
      )}

      <div className="border border-kult-border" data-testid="collections-list">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border bg-kult-black2 font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          <span className="col-span-1">#</span>
          <span className="col-span-3">SLUG</span>
          <span className="col-span-3">NAME (EN)</span>
          <span className="col-span-2">NAME (PT)</span>
          <span className="col-span-1">IN MENU</span>
          <span className="col-span-2 text-right">ACTIONS</span>
        </div>
        {items.length === 0 && (
          <p className="px-4 py-6 font-mono text-xs text-kult-muted">No collections yet.</p>
        )}
        {items.map((c) => (
          <div
            key={c.slug}
            className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border items-center"
            data-testid={`collection-row-${c.slug}`}
          >
            <span className="col-span-1 font-mono text-xs text-kult-muted">{c.sort_order ?? 0}</span>
            <span className="col-span-3 font-mono text-xs text-white">{c.slug}</span>
            <span className="col-span-3 font-display text-base">{c.name_en}</span>
            <span className="col-span-2 font-mono text-xs text-kult-muted">{c.name_pt}</span>
            <button
              onClick={() => toggleMenu(c)}
              className="col-span-1 inline-flex items-center gap-1 text-kult-yellow hover:text-kult-pink"
              data-testid={`collection-toggle-${c.slug}`}
              title="Toggle visibility in Shop dropdown"
            >
              {c.show_in_shop_menu === false ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <button
                onClick={() => startEdit(c)}
                className="p-1.5 border border-kult-border hover:border-kult-pink"
                data-testid={`collection-edit-${c.slug}`}
                title="Edit"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={() => remove(c)}
                className="p-1.5 border border-kult-border hover:border-kult-pink text-kult-pink"
                data-testid={`collection-delete-${c.slug}`}
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

function Field({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{label}</span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none"
      />
    </label>
  );
}
